import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Plus, Trash2, Search } from "lucide-react"
import { useInvoice, useCreateInvoice, useUpdateInvoice } from "@/hooks/use-invoices"
import { useProducts } from "@/hooks/use-products"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

interface LineItem {
  name: string
  description: string
  pkwiu: string
  quantity: number
  unit: string
  unit_price_net: number
  discount_percent: number
  vat_rate: number
  product_id?: string
}

const defaultItem: LineItem = {
  name: "",
  description: "",
  pkwiu: "",
  quantity: 1,
  unit: "szt.",
  unit_price_net: 0,
  discount_percent: 0,
  vat_rate: 23,
}

export function InvoiceFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id
  const { data: invoiceData, isLoading } = useInvoice(isEdit ? id : undefined)
  const createMutation = useCreateInvoice()
  const updateMutation = useUpdateInvoice()
  const { data: productsData } = useProducts()

  const [invoiceType, setInvoiceType] = useState("vat")
  const [currency, setCurrency] = useState("PLN")
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0])
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split("T")[0])
  const [dueDate, setDueDate] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("transfer")
  const [splitPayment, setSplitPayment] = useState(false)
  const [sellerName, setSellerName] = useState("OkeyCRM Sp. z o.o.")
  const [sellerNip, setSellerNip] = useState("")
  const [sellerAddress, setSellerAddress] = useState("")
  const [sellerBankName, setSellerBankName] = useState("")
  const [sellerBankAccount, setSellerBankAccount] = useState("")
  const [buyerName, setBuyerName] = useState("")
  const [buyerNip, setBuyerNip] = useState("")
  const [buyerAddress, setBuyerAddress] = useState("")
  const [notes, setNotes] = useState("")
  const [items, setItems] = useState<LineItem[]>([{ ...defaultItem }])
  const [productSearch, setProductSearch] = useState("")
  const [showProductPicker, setShowProductPicker] = useState<number | null>(null)

  // Set due date default to 14 days from issue
  useEffect(() => {
    if (!dueDate && issueDate) {
      const d = new Date(issueDate)
      d.setDate(d.getDate() + 14)
      setDueDate(d.toISOString().split("T")[0])
    }
  }, [issueDate, dueDate])

  // Load edit data
  useEffect(() => {
    if (isEdit && invoiceData?.data) {
      const inv = invoiceData.data
      setInvoiceType(inv.invoice_type)
      setCurrency(inv.currency)
      setIssueDate(inv.issue_date || "")
      setSaleDate(inv.sale_date || "")
      setDueDate(inv.due_date || "")
      setPaymentMethod(inv.payment_method)
      setSplitPayment(inv.split_payment)
      setSellerName(inv.seller_name || "")
      setSellerNip(inv.seller_nip || "")
      setSellerAddress(inv.seller_address || "")
      setSellerBankName(inv.seller_bank_name || "")
      setSellerBankAccount(inv.seller_bank_account || "")
      setBuyerName(inv.buyer_name || "")
      setBuyerNip(inv.buyer_nip || "")
      setBuyerAddress(inv.buyer_address || "")
      setNotes(inv.notes || "")
      if (inv.items.length > 0) {
        setItems(inv.items.map((it) => ({
          name: it.name,
          description: it.description || "",
          pkwiu: it.pkwiu || "",
          quantity: it.quantity,
          unit: it.unit,
          unit_price_net: it.unit_price_net,
          discount_percent: it.discount_percent,
          vat_rate: it.vat_rate,
          product_id: it.product_id || undefined,
        })))
      }
    }
  }, [isEdit, invoiceData])

  const calcItemNet = (item: LineItem) => {
    const discounted = item.unit_price_net * (1 - item.discount_percent / 100)
    return +(item.quantity * discounted).toFixed(2)
  }
  const calcItemVat = (item: LineItem) => +(calcItemNet(item) * item.vat_rate / 100).toFixed(2)
  const calcItemGross = (item: LineItem) => +(calcItemNet(item) + calcItemVat(item)).toFixed(2)

  const totalNet = items.reduce((s, it) => s + calcItemNet(it), 0)
  const totalVat = items.reduce((s, it) => s + calcItemVat(it), 0)
  const totalGross = +(totalNet + totalVat).toFixed(2)

  const addItem = () => setItems([...items, { ...defaultItem }])
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i))
  const updateItem = (i: number, field: keyof LineItem, value: string | number) => {
    const newItems = [...items]
    ;(newItems[i] as Record<string, unknown>)[field] = value
    setItems(newItems)
  }

  const selectProduct = (idx: number, product: { id: string; name: string; net_price: number; vat_rate: number; unit: string }) => {
    const newItems = [...items]
    newItems[idx] = {
      ...newItems[idx],
      product_id: product.id,
      name: product.name,
      unit_price_net: product.net_price,
      vat_rate: product.vat_rate,
      unit: product.unit || "szt.",
    }
    setItems(newItems)
    setShowProductPicker(null)
    setProductSearch("")
  }

  const products = productsData?.data || []
  const filteredProducts = products.filter((p) => p.name.toLowerCase().includes(productSearch.toLowerCase()))

  const handleSubmit = async () => {
    const validItems = items.filter((it) => it.name.trim())
    if (validItems.length === 0) {
      toast.error("Dodaj przynajmniej jedną pozycję")
      return
    }

    const payload: Record<string, unknown> = {
      invoice_type: invoiceType,
      currency,
      issue_date: issueDate,
      sale_date: saleDate,
      due_date: dueDate,
      payment_method: paymentMethod,
      split_payment: splitPayment,
      seller_name: sellerName,
      seller_nip: sellerNip,
      seller_address: sellerAddress,
      seller_bank_name: sellerBankName,
      seller_bank_account: sellerBankAccount,
      buyer_name: buyerName,
      buyer_nip: buyerNip,
      buyer_address: buyerAddress,
      notes,
      items: validItems,
    }

    if (isEdit) {
      await updateMutation.mutateAsync({ id: id!, data: payload })
      toast.success("Faktura zaktualizowana")
      navigate(`/invoices/${id}`)
    } else {
      const res = await createMutation.mutateAsync(payload)
      toast.success("Faktura utworzona")
      navigate(`/invoices/${res.data.id}`)
    }
  }

  const fmt = (n: number) => new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

  if (isEdit && isLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>
  }

  const fieldClass = "w-full mt-1 rounded-md border border-border px-3 py-2 text-sm text-foreground"

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/invoices")} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edycja faktury" : "Nowa faktura"}
        </h1>
      </div>

      {/* Invoice type & basics */}
      <div className="rounded-lg border border-border p-4 space-y-4">
        <h2 className="text-sm font-semibold text-foreground">Dane faktury</h2>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Typ</label>
            <select value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}
              className={fieldClass}>
              <option value="vat">Faktura VAT</option>
              <option value="proforma">Proforma</option>
              <option value="corrective">Korekta</option>
              <option value="margin">Marża</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Data wystawienia</label>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Data sprzedaży</label>
            <input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Termin płatności</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className={fieldClass} />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Waluta</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}
              className={fieldClass}>
              <option value="PLN">PLN</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Forma płatności</label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
              className={fieldClass}>
              <option value="transfer">Przelew</option>
              <option value="cash">Gotówka</option>
              <option value="card">Karta</option>
              <option value="other">Inne</option>
            </select>
          </div>
          <div className="col-span-2 flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground">
              <input type="checkbox" checked={splitPayment} onChange={(e) => setSplitPayment(e.target.checked)} className="rounded" />
              Mechanizm podzielonej płatności (MPP)
            </label>
          </div>
        </div>
      </div>

      {/* Seller & Buyer */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-4 space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Sprzedawca</h2>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nazwa</label>
            <input type="text" value={sellerName} onChange={(e) => setSellerName(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">NIP</label>
            <input type="text" value={sellerNip} onChange={(e) => setSellerNip(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Adres</label>
            <textarea value={sellerAddress} onChange={(e) => setSellerAddress(e.target.value)} rows={2}
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm text-foreground resize-none" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Bank</label>
            <input type="text" value={sellerBankName} onChange={(e) => setSellerBankName(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nr konta (IBAN)</label>
            <input type="text" value={sellerBankAccount} onChange={(e) => setSellerBankAccount(e.target.value)}
              className={`${fieldClass} font-mono`} />
          </div>
        </div>
        <div className="rounded-lg border border-border p-4 space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Nabywca</h2>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nazwa</label>
            <input type="text" value={buyerName} onChange={(e) => setBuyerName(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">NIP</label>
            <input type="text" value={buyerNip} onChange={(e) => setBuyerNip(e.target.value)}
              className={fieldClass} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Adres</label>
            <textarea value={buyerAddress} onChange={(e) => setBuyerAddress(e.target.value)} rows={2}
              className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm text-foreground resize-none" />
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Pozycje</h2>
          <Button variant="outline" size="sm" onClick={addItem}>
            <Plus size={14} className="mr-1" /> Dodaj pozycję
          </Button>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground pb-2 pr-2">Nazwa</th>
              <th className="text-right text-xs font-medium text-muted-foreground pb-2 px-1 w-16">Ilość</th>
              <th className="text-center text-xs font-medium text-muted-foreground pb-2 px-1 w-16">J.m.</th>
              <th className="text-right text-xs font-medium text-muted-foreground pb-2 px-1 w-24">Cena netto</th>
              <th className="text-right text-xs font-medium text-muted-foreground pb-2 px-1 w-16">Rabat %</th>
              <th className="text-right text-xs font-medium text-muted-foreground pb-2 px-1 w-16">VAT %</th>
              <th className="text-right text-xs font-medium text-muted-foreground pb-2 px-1 w-24">Netto</th>
              <th className="text-right text-xs font-medium text-muted-foreground pb-2 px-1 w-24">Brutto</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="group">
                <td className="pr-2 py-1 relative">
                  <div className="flex gap-1">
                    <input type="text" value={item.name} onChange={(e) => updateItem(i, "name", e.target.value)} placeholder="Nazwa pozycji"
                      className="flex-1 rounded-md border border-border px-2 py-1.5 text-sm text-foreground" />
                    <button onClick={() => { setShowProductPicker(showProductPicker === i ? null : i); setProductSearch("") }}
                      className="px-1.5 rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted/50">
                      <Search size={14} />
                    </button>
                  </div>
                  {showProductPicker === i && (
                    <div className="absolute z-10 top-full left-0 mt-1 w-80 rounded-lg border border-border bg-card shadow-lg">
                      <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Szukaj produktu..."
                        className="w-full border-b border-border px-3 py-2 text-sm text-foreground" autoFocus />
                      <div className="max-h-48 overflow-auto">
                        {filteredProducts.slice(0, 10).map((p) => (
                          <button key={p.id} onClick={() => selectProduct(i, p)}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 flex justify-between">
                            <span>{p.name}</span>
                            <span className="font-mono text-muted-foreground">{fmt(p.net_price)} {currency}</span>
                          </button>
                        ))}
                        {filteredProducts.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">Brak wyników</p>}
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-1 py-1">
                  <input type="number" value={item.quantity} onChange={(e) => updateItem(i, "quantity", +e.target.value)} min="0" step="0.01"
                    className="w-full text-right rounded-md border border-border px-2 py-1.5 text-sm text-foreground" />
                </td>
                <td className="px-1 py-1">
                  <input type="text" value={item.unit} onChange={(e) => updateItem(i, "unit", e.target.value)}
                    className="w-full text-center rounded-md border border-border px-1 py-1.5 text-sm text-foreground" />
                </td>
                <td className="px-1 py-1">
                  <input type="number" value={item.unit_price_net} onChange={(e) => updateItem(i, "unit_price_net", +e.target.value)} min="0" step="0.01"
                    className="w-full text-right rounded-md border border-border px-2 py-1.5 text-sm text-foreground" />
                </td>
                <td className="px-1 py-1">
                  <input type="number" value={item.discount_percent} onChange={(e) => updateItem(i, "discount_percent", +e.target.value)} min="0" max="100"
                    className="w-full text-right rounded-md border border-border px-2 py-1.5 text-sm text-foreground" />
                </td>
                <td className="px-1 py-1">
                  <select value={item.vat_rate} onChange={(e) => updateItem(i, "vat_rate", +e.target.value)}
                    className="w-full text-right rounded-md border border-border px-1 py-1.5 text-sm text-foreground">
                    <option value={23}>23%</option>
                    <option value={8}>8%</option>
                    <option value={5}>5%</option>
                    <option value={0}>0%</option>
                    <option value={-1}>zw.</option>
                  </select>
                </td>
                <td className="px-1 py-1 text-right font-mono text-sm text-foreground">{fmt(calcItemNet(item))}</td>
                <td className="px-1 py-1 text-right font-mono text-sm font-medium text-foreground">{fmt(calcItemGross(item))}</td>
                <td className="py-1">
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:bg-red-50">
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-1 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Suma netto:</span><span className="font-mono">{fmt(totalNet)} {currency}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>VAT:</span><span className="font-mono">{fmt(totalVat)} {currency}</span>
            </div>
            <div className="flex justify-between font-semibold border-t border-primary pt-1 text-primary">
              <span>Suma brutto:</span><span className="font-mono">{fmt(totalGross)} {currency}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-lg border border-border p-4 space-y-3">
        <h2 className="text-sm font-semibold text-foreground">Uwagi</h2>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Dodatkowe uwagi do faktury..."
          className="w-full rounded-md border border-border px-3 py-2 text-sm text-foreground resize-none" />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/invoices")}>Anuluj</Button>
        <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
          {createMutation.isPending || updateMutation.isPending ? "Zapisywanie..." : isEdit ? "Zapisz zmiany" : "Utwórz fakturę"}
        </Button>
      </div>
    </div>
  )
}
